import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ConfirmDialogService } from '../../dialog/confirm-dialog/confirm-dialog.service';
import { GeneralLedgerAccountService } from './generalledgeraccount.service';

/** Dependencies required to run the shared general ledger account delete flow. */
export interface DeleteGeneralLedgerAccountOptions {
  /** Id of the general ledger account to delete. */
  id: string;
  service: GeneralLedgerAccountService;
  dialogService: ConfirmDialogService;
  destroyRef: DestroyRef;
  /** Called after the general ledger account has been removed successfully. */
  onDeleted: () => void;
}

/**
 * Runs the shared "Verwijderen" flow for a general ledger account.
 * Shows a confirmation dialog ('Verwijderen?' / 'Grootboekrekening verwijderen?').
 * When 'Nee' is selected nothing happens and the dialog closes.
 * When 'Ja' is selected the account is removed via the service and onDeleted is called.
 */
export function deleteGeneralLedgerAccountWithChecks(
  options: DeleteGeneralLedgerAccountOptions,
): void {
  const { id, service, dialogService, destroyRef, onDeleted } = options;

  dialogService.open({
    width: '320px',
    title: 'Verwijderen?',
    message: 'Grootboekrekening verwijderen?',
    cancelText: 'Nee',
    confirmText: 'Ja',
    variant: 'error',
  });

  dialogService
    .confirmed()
    .pipe(
      take(1),
      switchMap((confirmed) => {
        if (!confirmed) {
          return EMPTY;
        }

        return service.removeGeneralLedgerAccount(id);
      }),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe({
      next: () => onDeleted(),
      error: () => undefined,
    });
}
